import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
    Button,
    CircularProgress,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    TextField,
    Typography,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { axios, apiRequestOptions, errorRequestHandler, transformHttpResponse } from 'utils';
import { apiPath } from 'constants/apiPathConstants';

const useStyles = makeStyles(theme => ({
    root: { marginBottom: theme.spacing(2), maxWidth: 720 },
    section: { marginTop: theme.spacing(1), fontWeight: 600 },
    none: { color: theme.palette.text.secondary, fontSize: 13 },
    error: { color: theme.palette.error.main, fontSize: 13, marginTop: theme.spacing(1) },
}));

const MIN_QUERY = 2;
const DEBOUNCE_MS = 400;
const pathOf = location => location.path.map(node => node.title).join(' › ');

/**
 * FINDING A PLACE, AND ADDING IT FROM GEONAMES (organiser, 18 Oct 2026: «шукай
 * місто в нашій системі, якщо воно є то показує нам що є, якщо нема, то шукає
 * його на сайті геонеймс, і добавляє із відповідною ієрархією»).
 *
 * Two lists, both the server's (LocationSearchController): «Вже в системі» —
 * our tree, with each place's path — and «Знайдено на GeoNames», each row
 * saying whether we already hold it. «Додати» imports the place with its
 * country and region; «Показати» opens the tree on it. GeoNames unreachable
 * (no internet in a hall) is said, beside the local list.
 *
 * Stable Playwright hooks: location-search, location-search-input,
 * location-local-row, location-geonames-row (data-geoname-id),
 * location-geonames-add, location-show, location-geonames-error
 */
const LocationSearch = ({ onReveal, onImported }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const [query, setQuery] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(null);
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const q = query.trim();
        if (q.length < MIN_QUERY) {
            setResult(null);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            setLoading(true);
            axios(apiRequestOptions(apiPath.LOCATIONS.SEARCH, { q }))
                .then(transformHttpResponse)
                .then(response => !cancelled && setResult(response.data))
                .catch(error => !cancelled && dispatch(errorRequestHandler(error)))
                .finally(() => !cancelled && setLoading(false));
        }, DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, revision, dispatch]);

    const add = row => {
        setAdding(row.geonameId);
        axios(apiRequestOptions(apiPath.LOCATIONS.IMPORT, { geonameId: row.geonameId }))
            .then(transformHttpResponse)
            .then(response => {
                onImported(response.data.location);
                setRevision(revision + 1);
            })
            .catch(error => dispatch(errorRequestHandler(error)))
            .finally(() => setAdding(null));
    };

    const describe = row => [row.regionName, row.countryName].filter(Boolean).join(', ')
        + (row.population ? ` · ${t('page.competitionSetting.locations.population', 'population')} ${row.population.toLocaleString('uk-UA')}` : '');

    return (
        <div className={classes.root} data-testid="location-search">
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                label={t('page.competitionSetting.locations.search', 'Find a place')}
                value={query}
                onChange={event => setQuery(event.target.value)}
                inputProps={{ 'data-testid': 'location-search-input' }}
                InputProps={{ endAdornment: loading ? <CircularProgress size={18} /> : null }}
            />
            {result && (
                <>
                    <Typography className={classes.section}>
                        {t('page.competitionSetting.locations.inSystem', 'Already in the system')}
                    </Typography>
                    {0 === result.local.length ? (
                        <Typography className={classes.none}>{t('page.competitionSetting.locations.notInSystem', 'Not in the system.')}</Typography>
                    ) : (
                        <List dense disablePadding>
                            {result.local.map(location => (
                                <ListItem key={location.id} ContainerProps={{ 'data-testid': 'location-local-row' }}>
                                    <ListItemText primary={pathOf(location)} />
                                    <ListItemSecondaryAction>
                                        <Button size="small" onClick={() => onReveal(location)} data-testid="location-show">
                                            {t('page.competitionSetting.locations.show', 'Show')}
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    <Typography className={classes.section}>
                        {t('page.competitionSetting.locations.onGeoNames', 'Found on GeoNames')}
                    </Typography>
                    {result.geonamesError && (
                        <Typography className={classes.error} data-testid="location-geonames-error">
                            {t(result.geonamesError, 'GeoNames is not reachable — only what is already in the system is shown.')}
                        </Typography>
                    )}
                    {!result.geonamesError && 0 === result.geonames.length && (
                        <Typography className={classes.none}>{t('page.competitionSetting.locations.nothingOnGeoNames', 'Nothing found.')}</Typography>
                    )}
                    <List dense disablePadding>
                        {result.geonames.map(row => (
                            <ListItem
                                key={row.geonameId}
                                // With a secondary action MUI wraps the row: the hooks go on
                                // the wrapper, so the button is inside them.
                                ContainerProps={{ 'data-testid': 'location-geonames-row', 'data-geoname-id': row.geonameId }}
                            >
                                <ListItemText
                                    primary={`${row.name} — ${t(`page.competitionSetting.locations.kinds.${row.kind}`, row.kind)}`}
                                    secondary={describe(row)}
                                />
                                <ListItemSecondaryAction>
                                    {row.existing ? (
                                        <Button size="small" onClick={() => onReveal(row.existing)} data-testid="location-show">
                                            {t('page.competitionSetting.locations.alreadyHere', 'Already here — show')}
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="contained"
                                            disabled={null !== adding}
                                            onClick={() => add(row)}
                                            data-testid="location-geonames-add"
                                        >
                                            {t('page.competitionSetting.locations.add', 'Add')}
                                        </Button>
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </>
            )}
        </div>
    );
};

LocationSearch.propTypes = {
    onReveal: PropTypes.func.isRequired,
    onImported: PropTypes.func.isRequired,
};

export default LocationSearch;
